/**
 * itemFields — the single source of truth for built-in item-list fields.
 *
 * Each built-in field is described ONCE here, with independent optional facets:
 *   - `column`  → the field is a table column (width, sort, label, default visibility)
 *   - `filter`  → the field is filterable (type, options, group, label)
 *   - `getValue`→ how to read the field's value for sort / group / filter matching / export
 *
 * The four historical constants are now DERIVED from this registry (so they can
 * no longer drift):
 *   - ALL_COLUMNS / DEFAULT_VISIBLE_COLUMNS  (hooks/useItemListState.ts)
 *   - COLUMN_CONFIG                          (exported here; consumed by ItemTable.tsx)
 *   - FILTER_FIELD_GROUPS                    (config/filterFields.ts)
 *   - getFieldValue                          (hooks/useItemListData.ts)
 *
 * Adding a built-in field is now a single entry here → it becomes a toggleable
 * column, a filter, sortable/groupable/value-resolvable, and saved-report
 * serializable, in the item list AND the custom-report designer, with no other
 * edits. The ONE remaining touch is a bespoke table CELL: a field that needs
 * custom rendering still requires a `case` in `renderDisplayContent` in
 * ItemTable.tsx (cell rendering deliberately stays in the component). Fields
 * that render as plain text/badges need no render case.
 *
 * IMPORTANT — this module must stay React/Zustand-free so `filterFields.ts` (a
 * deliberately dependency-free data module) can import from it. `ItemRow` is
 * imported as a TYPE only (erased at compile time), so no runtime dependency on
 * the data hook leaks into filter consumers. Dynamic workspace custom fields
 * (`meta:*`) are NOT in this static registry — they flow through the existing
 * `useAllColumns` / `buildCustomFieldsGroup` / `getFieldValue` default-branch path.
 */

import { CATEGORY_DOT } from '../../../../lib/itemCategoryColors';
import type { ItemRow } from '../hooks/useItemListData';

// ============================================================================
// Facet + field types
// ============================================================================

/** UI-level filter type — determines which value editor is rendered. */
export type FilterFieldType = 'set' | 'boolean' | 'text' | 'date-range' | 'range';

export interface FilterFieldOption {
  value: string;
  label: string;
  dot?: string;
}

/** Table-column facet (drives ALL_COLUMNS, the column picker, and COLUMN_CONFIG). */
export interface ItemColumnFacet {
  /** Column header / COLUMN_CONFIG label. */
  label: string;
  /** Whether the column is on by default (carried for ALL_COLUMNS shape; the actual
   * default set is the explicit DEFAULT_VISIBLE_ORDER, not this flag). */
  defaultVisible: boolean;
  width: string;
  defaultWidth: number;
  sortField: string | false;
  cellExtra?: string;
  /** false = rendered in the table but hidden from ALL_COLUMNS / the column picker (e.g. triage_action). Default true. */
  inPicker?: boolean;
  /** ALL_COLUMNS / picker label when it differs from `label` (COLUMN_CONFIG label). */
  pickerLabel?: string;
}

/** Filter facet (drives FILTER_FIELD_GROUPS). */
export interface ItemFilterFacet {
  label: string;
  group: string;
  type: FilterFieldType;
  description?: string;
  options?: FilterFieldOption[];
  /** When true, `options` are injected at render time from workspace data. */
  dynamicOptions?: boolean;
}

export interface ItemFieldDef {
  key: string;
  column?: ItemColumnFacet;
  filter?: ItemFilterFacet;
  /** Pure value resolver. Omit for fields resolved by the getFieldValue default branch (e.g. owner, metaCategory). */
  getValue?: (item: ItemRow) => unknown;
}

/** COLUMN_CONFIG value shape (consumed by ItemTable.tsx). */
export interface ColConfig {
  label: string;
  width: string;
  defaultWidth: number;
  sortField: string | false;
  cellExtra?: string;
}

// ============================================================================
// The registry
// ============================================================================
//
// Array order determines the order of fields WITHIN each filter group. Column
// order and the default-visible set are governed by the explicit order arrays
// below (COLUMN_ORDER / DEFAULT_VISIBLE_ORDER), not by array position.

export const ITEM_FIELDS: ItemFieldDef[] = [
  // ── Item (filter group) ────────────────────────────────────────────────────
  {
    key: 'itemCategory',
    column: { label: 'Category', defaultVisible: true, width: 'w-28', defaultWidth: 112, sortField: 'itemCategory' },
    filter: {
      label: 'Category', group: 'Item', type: 'set', description: 'Item language type',
      options: [
        { value: 'problem',     label: 'Problem',     dot: CATEGORY_DOT.problem },
        { value: 'opportunity', label: 'Opportunity', dot: CATEGORY_DOT.opportunity },
        { value: 'risk',        label: 'Risk',        dot: CATEGORY_DOT.risk },
        { value: 'solution',    label: 'Solution',    dot: CATEGORY_DOT.solution },
        { value: 'commitment',  label: 'Commitment',  dot: CATEGORY_DOT.commitment },
        { value: 'investment',  label: 'Investment',  dot: CATEGORY_DOT.investment },
      ],
    },
    getValue: (item) => item.itemCategory,
  },
  {
    key: 'metaCategory',
    filter: {
      label: 'Subject type', group: 'Item', type: 'set',
      description: 'What kind of thing is being prioritized',
      options: [
        { value: 'people',      label: 'People' },
        { value: 'places',      label: 'Places' },
        { value: 'things',      label: 'Things' },
        { value: 'activities',  label: 'Activities' },
        { value: 'information', label: 'Information' },
        { value: 'time',        label: 'Time' },
      ],
    },
    // value resolved via getFieldValue default branch (item.metaCategory)
  },
  {
    key: 'exit_status',
    column: { label: 'Status', defaultVisible: false, width: 'w-32', defaultWidth: 128, sortField: 'exit_status' },
    filter: {
      label: 'Status', group: 'Item', type: 'set', description: 'Is this item in active consideration?',
      options: [
        { value: 'active',   label: 'Active',   dot: 'bg-green-400' },
        { value: 'deferred', label: 'Deferred', dot: 'bg-amber-400' },
        { value: 'archived', label: 'Archived', dot: 'bg-gray-400' },
      ],
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getValue: (item) => (item as any).exit_status ?? 'active',
  },
  {
    key: 'altitude',
    column: { label: 'Altitude', defaultVisible: true, width: 'w-32', defaultWidth: 128, sortField: 'altitude' },
    filter: {
      label: 'Altitude', group: 'Item', type: 'set', description: 'Work level',
      options: [
        { value: '1', label: 'Level 1' },
        { value: '2', label: 'Level 2' },
        { value: '3', label: 'Level 3' },
        { value: '4', label: 'Level 4' },
        { value: '5', label: 'Level 5' },
      ],
    },
    getValue: (item) => item.altitudeLevel,
  },
  {
    key: 'source',
    column: { label: 'Source', defaultVisible: false, width: 'w-24', defaultWidth: 96, sortField: 'source' },
    filter: {
      label: 'Source', group: 'Item', type: 'set', description: 'Origin of the idea or request',
      options: [
        { value: 'customer_feedback',   label: 'Customer Feedback' },
        { value: 'team_discussion',     label: 'Team Discussion' },
        { value: 'stakeholder_request', label: 'Stakeholder Request' },
        { value: 'market_research',     label: 'Market Research' },
        { value: 'competitor_analysis', label: 'Competitor Analysis' },
        { value: 'internal_ops',        label: 'Internal Operations' },
        { value: 'strategic_initiative',label: 'Strategic Initiative' },
        { value: 'bug_or_issue',        label: 'Bug or Issue' },
        { value: 'other',              label: 'Other' },
      ],
    },
    getValue: (item) => item.source,
  },
  {
    key: 'strategicBucket',
    column: { label: 'Bucket', pickerLabel: 'Strategic Bucket', defaultVisible: false, width: 'w-32', defaultWidth: 128, sortField: 'strategicBucket' },
    filter: {
      label: 'Strategic Bucket', group: 'Item', type: 'set',
      description: 'Which strategic theme this item falls under',
      options: [
        { value: 'growth',       label: 'Growth' },
        { value: 'commitments',  label: 'Commitments' },
        { value: 'reliability',  label: 'Reliability' },
        { value: 'platform',     label: 'Platform' },
        { value: 'compliance',   label: 'Compliance' },
      ],
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getValue: (item) => (item as any).strategicBucket ?? null,
  },
  {
    key: 'tags',
    column: { label: 'Tags', defaultVisible: false, width: 'w-40', defaultWidth: 160, sortField: false },
    filter: { label: 'Tags', group: 'Item', type: 'text', description: 'Comma-separated labels attached to the item' },
    getValue: (item) => item.tags?.join(', '),
  },
  {
    key: 'dedupeStatus',
    column: { label: 'Dedupe', defaultVisible: false, width: 'w-28', defaultWidth: 112, sortField: 'dedupeStatus' },
    filter: {
      label: 'Dedupe Status', group: 'Item', type: 'set', description: 'Duplicate detection result',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'unique', label: 'Unique' },
        { value: 'merged', label: 'Merged' },
        { value: 'duplicate_of', label: 'Duplicate' },
      ],
    },
    getValue: (item) => item.dedupeStatus,
  },
  {
    key: 'classificationStatus',
    filter: {
      label: 'ATC Status', group: 'Item', type: 'set',
      description: 'Air Traffic Controller classification proposal status',
      options: [
        { value: 'empty',    label: 'Not classified',  dot: 'bg-gray-300' },
        { value: 'proposed', label: 'Proposal pending', dot: 'bg-amber-400' },
        { value: 'approved', label: 'Accepted',         dot: 'bg-green-400' },
      ],
    },
    getValue: (item) => item.classificationStatus ?? 'empty',
  },
  {
    key: 'creationProvenance',
    column: { label: 'Auth', defaultVisible: true, width: 'w-14', defaultWidth: 56, sortField: 'creationProvenance', cellExtra: 'justify-center' },
    filter: {
      label: 'Creation source', group: 'Item', type: 'set',
      description: 'Whether the item was created by a human or drafted by AI',
      options: [
        { value: 'human',       label: 'Human',       dot: 'bg-gray-300' },
        { value: 'ai_draft',    label: 'AI Draft',    dot: 'bg-[#D4A017]' },
        { value: 'ai_approved', label: 'AI Approved', dot: 'bg-[#3A7D56]' },
      ],
    },
    getValue: (item) => item.creationProvenance ?? 'human',
  },

  // ── Completeness (filter group) ─────────────────────────────────────────────
  {
    key: 'readyForRanking',
    filter: { label: 'Base complete', group: 'Completeness', type: 'boolean', description: 'Has title, category, altitude, and dedupe decision' },
    getValue: (item) => item.hasBaseCompleteness,
  },
  {
    key: 'hasDescription',
    filter: { label: 'Has Description', group: 'Completeness', type: 'boolean', description: 'Item has description text filled in' },
    getValue: (item) => item.hasDescription,
  },
  {
    key: 'hasParentItem',
    filter: { label: 'Has Parent Item', group: 'Completeness', type: 'boolean', description: 'This item is a child of another item' },
    getValue: (item) => item.hasParentItem,
  },
  {
    key: 'isStale',
    filter: { label: 'Stale', group: 'Completeness', type: 'boolean', description: 'Not updated in the last 30 days' },
    getValue: (item) => item.isStale,
  },
  {
    key: 'pipelineStage',
    filter: {
      label: 'Pipeline stage', group: 'Completeness', type: 'set',
      description: 'The furthest point the item has reached, or the exit it took — as counted on the Pipeline Health report',
      options: [
        { value: 'awaiting_triage',  label: 'Awaiting triage',       dot: 'bg-[var(--text-dim)]' },
        { value: 'needs_definition', label: 'Needs definition',      dot: 'bg-[var(--mustard)]' },
        { value: 'ready',            label: 'Ready to prioritize',   dot: 'bg-[var(--slate)]' },
        { value: 'pending_result',   label: 'In a pool or session',  dot: 'bg-[var(--slate)]' },
        { value: 'ranked',           label: 'Ranked',                dot: 'bg-[var(--green)]' },
        { value: 'committed',        label: 'Committed',             dot: 'bg-[var(--green)]' },
        { value: 'deferred',         label: 'Deferred',              dot: 'bg-[var(--mustard)]' },
        { value: 'archived',         label: 'Archived',              dot: 'bg-[var(--text-dim)]' },
      ],
    },
    getValue: (item) => item.pipelineStage,
  },

  // ── Collection (filter group) ───────────────────────────────────────────────
  {
    key: 'inCollection',
    filter: { label: 'In Collection', group: 'Collection', type: 'set', description: 'Belongs to a specific collection', dynamicOptions: true },
    getValue: (item) => item.collectionIds,
  },
  {
    key: 'hasCollection',
    filter: { label: 'In Any Collection', group: 'Collection', type: 'boolean', description: 'Belongs to at least one collection' },
    getValue: (item) => item.hasCollection,
  },

  // ── Relationships (filter group) ────────────────────────────────────────────
  {
    key: 'inPackage',
    filter: { label: 'In Package', group: 'Relationships', type: 'set', description: 'Item belongs to a specific Package group', dynamicOptions: true },
    getValue: (item) => item.packageGroupIds,
  },
  {
    key: 'hasPackage',
    filter: { label: 'In Any Package', group: 'Relationships', type: 'boolean', description: 'Must be prioritized or selected together' },
    getValue: (item) => item.hasPackage,
  },
  {
    key: 'inCluster',
    filter: { label: 'In Cluster', group: 'Relationships', type: 'set', description: 'Item belongs to a specific Cluster group', dynamicOptions: true },
    getValue: (item) => item.clusterGroupIds,
  },
  {
    key: 'hasCluster',
    filter: { label: 'In Any Cluster', group: 'Relationships', type: 'boolean', description: 'Grouped by identity with other items' },
    getValue: (item) => item.hasCluster,
  },
  {
    key: 'inExclusive',
    filter: { label: 'In Competing Group', group: 'Relationships', type: 'set', description: 'Item belongs to a specific Competing (Exclusive) group', dynamicOptions: true },
    getValue: (item) => item.exclusiveGroupIds,
  },
  {
    key: 'hasCompeting',
    filter: { label: 'Has Any Competing', group: 'Relationships', type: 'boolean', description: 'Mutually exclusive with one or more other items' },
    getValue: (item) => item.hasCompeting,
  },
  {
    key: 'hasPredecessors',
    filter: { label: 'Has predecessors', group: 'Relationships', type: 'boolean', description: 'Must be addressed after something else (sequence / conditions)' },
    getValue: (item) => item.hasPredecessors,
  },
  {
    key: 'hasDependents',
    filter: { label: 'Has dependents', group: 'Relationships', type: 'boolean', description: 'Items waiting on this item (sequence / conditions)' },
    getValue: (item) => item.hasDependents,
  },
  {
    key: 'hasCompositionParts',
    filter: { label: 'Has composition parts', group: 'Relationships', type: 'boolean', description: 'This item is a whole with one or more parts' },
    getValue: (item) => item.hasCompositionParts,
  },
  {
    key: 'isPartOfComposition',
    filter: { label: 'Is part of a whole', group: 'Relationships', type: 'boolean', description: 'This item is a part in a composition hierarchy' },
    getValue: (item) => item.isPartOfComposition,
  },
  {
    key: 'hasElaborationPossibilities',
    filter: { label: 'Has elaboration possibilities', group: 'Relationships', type: 'boolean', description: 'Parent need with a possibility continuum' },
    getValue: (item) => item.hasElaborationPossibilities,
  },
  {
    key: 'isElaborationPossibility',
    filter: { label: 'Is an elaboration possibility', group: 'Relationships', type: 'boolean', description: 'This item is a possibility on a parent need continuum' },
    getValue: (item) => item.isElaborationPossibility,
  },
  {
    key: 'hasContainsChildren',
    filter: { label: 'Has contains children', group: 'Relationships', type: 'boolean', description: 'Reporting parent with child items' },
    getValue: (item) => item.hasContainsChildren,
  },
  {
    key: 'hasContainsParent',
    filter: { label: 'Has contains parent', group: 'Relationships', type: 'boolean', description: 'Reporting child of a parent item' },
    getValue: (item) => item.hasContainsParent,
  },
  {
    key: 'hasRelated',
    filter: { label: 'Has Related', group: 'Relationships', type: 'boolean', description: 'Connected to another item by proximity or association' },
    getValue: (item) => item.hasRelated,
  },
  {
    key: 'hasFrames',
    filter: { label: 'Has Additional Categories', group: 'Relationships', type: 'boolean', description: 'Item has more than one category' },
    getValue: (item) => item.hasFrames,
  },

  // ── Lineage (filter group) ──────────────────────────────────────────────────
  {
    key: 'isLeadershipApproved',
    filter: { label: 'On Priority List', group: 'Lineage', type: 'boolean', description: 'Approved by leadership via Catchball or item pool' },
    getValue: (item) => item.isLeadershipApproved,
  },
  {
    key: 'inItemPool',
    filter: { label: 'In Item Pool', group: 'Lineage', type: 'boolean', description: 'Curated into at least one prioritization pool' },
    getValue: (item) => item.inItemPool,
  },
  {
    key: 'hasCatchballProposal',
    filter: { label: 'Has Catchball Proposal', group: 'Lineage', type: 'boolean', description: 'A proposal has been submitted for this item' },
    getValue: (item) => item.hasCatchballProposal,
  },
  {
    key: 'catchballStatus',
    filter: {
      label: 'Catchball Outcome', group: 'Lineage', type: 'set', description: 'Most recent Catchball proposal status',
      options: [
        { value: 'pending',   label: 'Pending',   dot: 'bg-amber-400' },
        { value: 'accepted',  label: 'Accepted',  dot: 'bg-green-400' },
        { value: 'countered', label: 'Returned',  dot: 'bg-orange-400' },
        { value: 'rejected',  label: 'Rejected',  dot: 'bg-red-400' },
        { value: 'withdrawn', label: 'Withdrawn', dot: 'bg-gray-400' },
      ],
    },
    getValue: (item) => (item.catchballStatus === 'none' ? null : item.catchballStatus),
  },
  {
    key: 'hasLineage',
    filter: { label: 'Has Lineage', group: 'Lineage', type: 'boolean', description: 'Item has undergone a split, merge, fork, supersede, or retire operation' },
    getValue: (item) => item.hasLineage,
  },

  // ── Activity (filter group) ─────────────────────────────────────────────────
  {
    key: 'cycleId',
    filter: { label: 'Cycle', group: 'Activity', type: 'set', description: 'Prioritization cycle containing the item', dynamicOptions: true },
    getValue: (item) => item.cycleIds,
  },
  {
    key: 'trackId',
    filter: { label: 'Track', group: 'Activity', type: 'set', description: 'Track whose item pool contains the item', dynamicOptions: true },
    getValue: (item) => item.trackIds,
  },
  {
    key: 'sessionId',
    filter: { label: 'Session', group: 'Activity', type: 'set', description: 'Ranking session that included the item', dynamicOptions: true },
    getValue: (item) => item.sessionIds,
  },

  // ── Queue (filter group + column) ───────────────────────────────────────────
  {
    key: 'queue',
    column: { label: 'Queue', defaultVisible: true, width: 'w-28', defaultWidth: 112, sortField: 'queue' },
    filter: { label: 'Queue', group: 'Queue', type: 'set', description: 'Org unit the item was submitted to', dynamicOptions: true },
    getValue: (item) => item.queueId,
  },
  {
    key: 'orgUnit',
    // Filter + value only: the item table has no renderer for it, so a column would show raw ids.
    filter: { label: 'Org unit', group: 'Queue', type: 'set', description: 'Org unit the item belongs to', dynamicOptions: true },
    getValue: (item) => item.orgUnitId,
  },
  {
    key: 'ownerId',
    // The Owner column (key 'owner') shows the name; this is the filter on the same person, by id.
    filter: { label: 'Owner', group: 'Queue', type: 'set', description: 'Person who owns the item', dynamicOptions: true },
    getValue: (item) => item.ownerId,
  },

  // ── Dates (filter group + columns) ──────────────────────────────────────────
  {
    key: 'createdAt',
    column: { label: 'Created', defaultVisible: true, width: 'w-24', defaultWidth: 96, sortField: 'createdAt' },
    filter: { label: 'Created Date', group: 'Dates', type: 'date-range', description: 'When the item was created' },
    getValue: (item) => item.createdAt,
  },
  {
    key: 'updatedAt',
    column: { label: 'Updated', defaultVisible: false, width: 'w-24', defaultWidth: 96, sortField: 'updatedAt' },
    filter: { label: 'Updated Date', group: 'Dates', type: 'date-range', description: 'When the item was last modified' },
    getValue: (item) => item.updatedAt,
  },

  // ── Column-only fields (not filters) ────────────────────────────────────────
  {
    key: 'title',
    column: { label: 'Title', defaultVisible: true, width: 'min-w-[160px]', defaultWidth: 280, sortField: 'title', cellExtra: 'overflow-hidden' },
    getValue: (item) => item.title,
  },
  {
    key: 'readiness',
    column: { label: 'Gaps', defaultVisible: true, width: 'w-32', defaultWidth: 128, sortField: 'readiness', cellExtra: 'overflow-hidden' },
    getValue: (item) => item.missingBaseFields,
  },
  {
    key: 'relationships',
    column: { label: 'Relationships', defaultVisible: true, width: 'w-36', defaultWidth: 144, sortField: false },
  },
  {
    key: 'owner',
    column: { label: 'Owner', defaultVisible: true, width: 'w-32', defaultWidth: 128, sortField: 'ownerId' },
    // value resolved via getFieldValue default branch (item.owner)
  },
  {
    key: 'createdBy',
    column: { label: 'Created By', defaultVisible: false, width: 'w-32', defaultWidth: 128, sortField: 'createdBy' },
    getValue: (item) => item.createdBy,
  },
  {
    key: 'collections',
    column: { label: 'Collections', defaultVisible: false, width: 'w-40', defaultWidth: 160, sortField: false, cellExtra: 'overflow-hidden' },
    // rendered from collectionNames in ItemTable; no scalar getValue
  },
  {
    key: 'triage_action',
    column: { label: 'Decision', defaultVisible: false, width: 'w-44', defaultWidth: 176, sortField: false, inPicker: false },
  },

  // ── Value-only fields (sort / group / legacy aliases) ───────────────────────
  { key: 'description', getValue: (item) => item.description },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { key: 'detailedState', getValue: (item) => (item as any).exit_status ?? 'active' }, // legacy alias
  { key: 'lifecycle_state', getValue: (item) => item.lifecycle_state },
  { key: 'lifecycle_stage', getValue: (item) => item.lifecycle_stage },
  { key: 'steward', getValue: (item) => item.stewardship?.assignedStewardId ?? item.shapingOwnerId ?? item.stewardId },
  { key: 'shapingCompleteness', getValue: (item) => item.missingBaseFields },
  { key: 'hasDependency', getValue: (item) => item.hasDependency },
  { key: 'hasAggregation', getValue: (item) => item.hasAggregation },
  { key: 'hasElaboration', getValue: (item) => item.hasElaboration },
  { key: 'collection', getValue: (item) => item.collectionIds },
];

// ============================================================================
// Lookups + explicit ordering
// ============================================================================

export const ITEM_FIELD_BY_KEY: Record<string, ItemFieldDef> = Object.fromEntries(
  ITEM_FIELDS.map((f) => [f.key, f])
);

/** Column order for ALL_COLUMNS / the column picker (excludes non-picker columns like triage_action). */
export const COLUMN_ORDER: string[] = [
  'title',
  'creationProvenance',
  'itemCategory',
  'exit_status',
  'altitude',
  'queue',
  'readiness',
  'relationships',
  'owner',
  'createdAt',
  'updatedAt',
  'createdBy',
  'collections',
  'strategicBucket',
  'source',
  'tags',
  'dedupeStatus',
];

/** Default-visible column set + order. Independent of ALL_COLUMNS order and of the `defaultVisible` flag. */
export const DEFAULT_VISIBLE_ORDER: string[] = [
  'title',
  'creationProvenance',
  'itemCategory',
  'altitude',
  'queue',
  'owner',
  'readiness',
  'relationships',
  'createdAt',
];

/** Filter group order on the filter panel. */
export const FILTER_GROUP_ORDER: string[] = [
  'Item',
  'Completeness',
  'Collection',
  'Relationships',
  'Lineage',
  'Activity',
  'Queue',
  'Dates',
];

// ============================================================================
// Derived: COLUMN_CONFIG (consumed by ItemTable.tsx)
// ============================================================================

export const COLUMN_CONFIG: Record<string, ColConfig> = Object.fromEntries(
  ITEM_FIELDS.filter((f) => f.column).map((f) => {
    const c = f.column!;
    const cfg: ColConfig = { label: c.label, width: c.width, defaultWidth: c.defaultWidth, sortField: c.sortField };
    if (c.cellExtra !== undefined) cfg.cellExtra = c.cellExtra;
    return [f.key, cfg];
  })
);
